import React, { useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Target,
  AlertCircle,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { Button, SearchSelect, PillToggle } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import CreateServiceCategoryModal from '../../components/admin/CreateServiceCategoryModal';
import { useToastContext } from "../../contexts/ToastContext";

import { 
  useAdminServiceCategories, 
  useCreateServiceCategory, 
  useUpdateServiceCategory, 
  useDeleteServiceCategory, 
  useToggleCategoryStatus,
  SERVICE_TYPES 
} from '../../services/serviceCategoryService';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
const AdminServiceCategories = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
//   const { showToast } = useToastContext();
  const { success, error: showError } = useToastContext();
  // API query parameters
  const queryParams = {
    active: statusFilter === 'all' ? undefined : statusFilter === 'active',
    service_type: typeFilter === 'all' ? undefined : typeFilter,
    sort_by: 'sort_order',
    sort_order: 'asc'
  };

  // Fetch categories from API
  const { 
    data: categoriesData, 
    isLoading: loading, 
    error: categoriesError,
    refetch 
  } = useAdminServiceCategories(queryParams);

  // Extract categories from API response
  const categories = categoriesData?.data?.data || [];

  // API mutations
  const createCategoryMutation = useCreateServiceCategory({
    onSuccess: () => {
        success('Service category created successfully', 'success');
      setShowCreateModal(false);
    },
    onError: (error) => {
        showError(error.message || 'Failed to create service category', 'error');
    }
  });

  const updateCategoryMutation = useUpdateServiceCategory({
    onSuccess: () => {
        success('Service category updated successfully', 'success');
      setShowEditModal(false);
      setSelectedCategory(null);
    },
    onError: (error) => {
        showError(error.message || 'Failed to update service category', 'error');
    }
  });

  const deleteCategoryMutation = useDeleteServiceCategory({
    onSuccess: () => {
        success('Service category deleted successfully', 'success');
    },
    onError: (error) => {
        showError(error.message || 'Failed to delete service category', 'error');
    }
  });

  const toggleStatusMutation = useToggleCategoryStatus({
    onSuccess: () => {
    //   showToast('Category status updated successfully', 'success');
      success('Category status updated successfully');
    },
    onError: (error) => {
        showError(error.message || 'Failed to update category status', 'error');
    }
  });

  // Client-side filtering for search (API handles status and type filtering)
  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.category_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.category_description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const isStatusToggling = (categoryId) =>
    toggleStatusMutation.isPending && toggleStatusMutation.variables === categoryId;

  const handleToggleStatus = (categoryId) => {
    toggleStatusMutation.mutate(categoryId);
  };

  const handleDeleteCategory = (categoryId) => {
    if (window.confirm('Are you sure you want to delete this service category?')) {
      deleteCategoryMutation.mutate(categoryId);
    }
  };

  const openEditModal = (category) => {
    setSelectedCategory(category);
    setShowEditModal(true);
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'category',
      label: 'Category',
      render: (category) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gray-900 dark:bg-gray-100 flex items-center justify-center">
              {category.emoji ? (
                <span className="text-lg">{category.emoji}</span>
              ) : (
                <Target className="h-5 w-5 text-white dark:text-gray-900" />
              )}
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {category.category_name}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {category.category_description}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'emoji',
      label: 'Emoji',
      render: (category) => (
        <div className="text-center">
          {category.emoji ? (
            <span className="text-2xl">{category.emoji}</span>
          ) : (
            <span className="text-gray-400 text-sm">No emoji</span>
          )}
        </div>
      )
    },
    {
      key: 'service_type',
      label: 'Type',
      render: (category) => (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300">
          {getServiceTypeLabel(category.service_type)}
        </span>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (category) => (
        <PillToggle
          checked={Boolean(category.is_active)}
          onChange={() => handleToggleStatus(category.id)}
          loading={isStatusToggling(category.id)}
          variant="success"
          onLabel="On"
          offLabel="Off"
          ariaLabel={`${category.category_name} active status`}
        />
      ),
    },
    {
      key: 'sort_order',
      label: 'Sort Order',
      render: (category) => (
        <span className="text-sm text-gray-900 dark:text-gray-50">{category.sort_order}</span>
      )
    }
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      onClick: (category) => openEditModal(category),
      title: 'Edit Category',
      variant: 'default',
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (category) => handleDeleteCategory(category.id),
      title: 'Delete Category',
      variant: 'danger',
    },
  ];

  const handleCreateCategory = (categoryData) => {
    createCategoryMutation.mutate(categoryData);
  };

  const handleEditCategory = (categoryData) => {
    updateCategoryMutation.mutate({
      id: selectedCategory.id,
      data: categoryData
    });
  };

  const getServiceTypeLabel = (type) => {
    const serviceType = SERVICE_TYPES.find(st => st.value === type);
    return serviceType ? serviceType.label : type;
  };

  if (categoriesError) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error Loading Categories</h3>
          <p className="text-red-600 dark:text-red-300 text-sm mb-6">
            {categoriesError.message || 'Failed to load service categories'}
          </p>
          <Button className="!rounded-xl" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Service Categories"
        subtitle="Manage service categories for the platform."
        actions={
          <Button
            onClick={() => setShowCreateModal(true)}
            className="!rounded-xl flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.serviceCategories.title}
        steps={ADMIN_PAGE_GUIDES.serviceCategories.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <div className="w-full sm:w-52">
                <SearchSelect
                  label="Status Filter"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' }
                  ]}
                  placeholder="Filter by status"
                />
              </div>
              <div className="w-full sm:w-52">
                <SearchSelect
                  label="Type Filter"
                  value={typeFilter}
                  onChange={setTypeFilter}
                  options={[
                    { value: 'all', label: 'All Types' },
                    ...SERVICE_TYPES
                  ]}
                  placeholder="Filter by type"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading service categories…</p>
            </div>
          ) : (
            <DataTable
              data={filteredCategories}
              columns={columns}
              actions={actions}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search categories..."
              className="!border-0 !shadow-none"
              emptyState={{
                icon: AlertCircle,
                title: "No categories found",
                description: searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                      ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by creating a new service category.',
                action: (!searchTerm && statusFilter === 'all' && typeFilter === 'all') ? (
                      <Button className="!rounded-xl" onClick={() => setShowCreateModal(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Category
                      </Button>
                ) : null
              }}
            />
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateServiceCategoryModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateCategory}
          serviceTypes={SERVICE_TYPES}
          isLoading={createCategoryMutation.isLoading}
        />
      )}

      {showEditModal && selectedCategory && (
        <CreateServiceCategoryModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCategory(null);
          }}
          onSave={handleEditCategory}
          serviceTypes={SERVICE_TYPES}
          category={selectedCategory}
          isEdit={true}
          isLoading={updateCategoryMutation.isLoading}
        />
      )}
    </div>
  );
};

export default AdminServiceCategories;
